import { useState } from "react";
import { createEntityStore } from "../../common/entityStore";
import { PageTitle } from "../../layout/PageTitle";
import { Button, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { propDateField, propNumberField, propTextField } from "../../common/entityStoreForm";
import { ClearButton, SearchButton } from "../../common/listStoreUi";
import { createAxios } from "../../common/axios";
import { formatNumber } from "../../common/localization";
import { useDefaultValues } from "../../common/defaultValues";
import { download } from "../../common/download";
import { today } from "../../common/utils";

export function TmRewardsReportPage() {
    const landCode = useDefaultValues(p => p.clientData.landCode);
    const [useParamsStore] = useState(() => createEntityStore<TmRewardsReportParamsDto>({ data: getNewData() }));
    const [useReportStore] = useState(() => createEntityStore<TmRewardsReportDto>({
        path: "reports/tmRewardsReport",
        data: {},
        fetchOnInit: false,
        customFetch: (path, signal, qs) => createAxios().post(path, useParamsStore.getState().clientData, { signal, params: qs }),
    }));
    const { isFetching, hasResult } = useReportStore(p => ({ isFetching: p.isFetching, hasResult: p.serverData.file != null }));
    function search() {
        useReportStore.getState().fetchServerData();
    }
    function clear() {
        useParamsStore.getState().setClientData(getNewData());
    }
    function getNewData(): TmRewardsReportParamsDto {
        return {
            fromDate: today(),
            toDate: today(),
            mailId: null,
            projectCode: "",
            mediaCode: "",
        };
    }
    async function exportResults() {
        var data = useReportStore.getState().serverData;
        download(data.fileName!, data.file!, data.fileType!);
    };
    var stats = useReportStore(p => p.serverData);
    return <>
        <PageTitle title="TM rewards report" />
        <Grid container columnSpacing={2}>
            <Grid item xs>{propDateField(useParamsStore)("fromDate", "fromDateFormatted", "From date")}</Grid>
            <Grid item xs>{propDateField(useParamsStore)("toDate", "toDateFormatted", "To date")}</Grid>
            <Grid item xs>{propNumberField(useParamsStore)("mailId", "mailIdFormatted", "Mail ID")}</Grid>
            <Grid item xs>{propTextField(useParamsStore)("projectCode", "Project code")}</Grid>
            <Grid item xs>{propTextField(useParamsStore)("mediaCode", "Media code")}</Grid>
            <SearchButton onClick={search} disabled={isFetching} />
            <ClearButton onClick={clear} disabled={isFetching} />
        </Grid>
        <Button onClick={exportResults} disabled={isFetching || !hasResult} title="Export results">Export</Button>
        <TableContainer component={Paper} sx={{ width: "650px", marginTop: "20px" }}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: "80px" }}>Statistics</TableCell>
                        <TableCell sx={{ width: "120px" }} align="right">WP</TableCell>
                        <TableCell sx={{ width: "120px" }} align="right">1st RS</TableCell>
                        <TableCell sx={{ width: "120px" }} align="right">2nd RS</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell component="th" scope="row">Total</TableCell>
                        <TableCell align="right">{formatNumber(stats.totalWp, landCode, false)}</TableCell>
                        <TableCell align="right">{formatNumber(stats.total1stRs, landCode, false)}</TableCell>
                        <TableCell align="right">{formatNumber(stats.total2ndRs, landCode, false)}</TableCell>
                    </TableRow>
                    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell component="th" scope="row">Paid</TableCell>
                        <TableCell align="right">{formatNumber(stats.paidWp, landCode, false)}</TableCell>
                        <TableCell align="right">{formatNumber(stats.paid1stRs, landCode, false)}</TableCell>
                        <TableCell align="right">{formatNumber(stats.paid2ndRs, landCode, false)}</TableCell>
                    </TableRow>
                    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell component="th" scope="row">%</TableCell>
                        <TableCell align="right">{formatNumber(stats.percentWp, landCode, false)}</TableCell>
                        <TableCell align="right">{formatNumber(stats.percent1stRs, landCode, false)}</TableCell>
                        <TableCell align="right">{formatNumber(stats.percent2ndRs, landCode, false)}</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    </>;
}

interface TmRewardsReportParamsDto {
    fromDate: string;
    toDate: string;
    mailId: number | null;
    projectCode: string;
    mediaCode: string;
    mailIdFormatted?: string;
    fromDateFormatted?: string;
    toDateFormatted?: string;
};

export interface TmRewardsReportDto {
    totalWp?: number;
    total1stRs?: number;
    total2ndRs?: number;
    paidWp?: number;
    paid1stRs?: number;
    paid2ndRs?: number;
    percentWp?: number;
    percent1stRs?: number;
    percent2ndRs?: number;
    file?: string;
    fileName?: string;
    fileType?: string;
}
